import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { ArrowLeft, MapPin, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsOutCardinal, Wine, Leaf, ForkKnife, Globe } from '@phosphor-icons/react';
import InteractiveWineMap from '../Maps/InteractiveWineMap';
import { Breadcrumb } from '../Product/Breadcrumb';

// Import region data functions
import { getProvenceData } from '../../data/regions/provence';
import { getBordeauxData } from '../../data/regions/bordeaux';
import { getBourgogneData } from '../../data/regions/bourgogne';
import { getJuraData } from '../../data/regions/jura';
import { getChampagneData } from '../../data/regions/champagne';
import { getLoireData } from '../../data/regions/loire';
import { getRhoneData } from '../../data/regions/rhone';
import { getLanguedocRoussillonData } from '../../data/regions/languedoc-roussillon';
import { getAlsaceData } from '../../data/regions/alsace';
import { getSudOuestData } from '../../data/regions/sud-ouest';
import { getRiojaData } from '../../data/regions/rioja';
import { getRiberaDelDueroData } from '../../data/regions/ribera-del-duero';
import { getNavarraData } from '../../data/regions/navarra';
import { getPrioratData } from '../../data/regions/priorat';
import { getRiasBaixasData } from '../../data/regions/rias-baixas';
import { getRuedaData } from '../../data/regions/rueda';
import { getCavaData } from '../../data/regions/cava';
import { getJerezData } from '../../data/regions/jerez';
import { getDouroData } from '../../data/regions/douro';
import { getAlentejoData } from '../../data/regions/alentejo';
import { getDaoData } from '../../data/regions/dao';
import { getVinhoVerdeData } from '../../data/regions/vinho-verde';
import { getBairradaData } from '../../data/regions/bairrada';
import { getLisboaData } from '../../data/regions/lisboa';
import { getSetubalData } from '../../data/regions/setubal';
import { getMadeiraData } from '../../data/regions/madeira';
import { getToscaneData } from '../../data/regions/toscane';
import { getPiemontData } from '../../data/regions/piemont';
import { getVenetieData } from '../../data/regions/venetie';
import { getSicileData } from '../../data/regions/sicile';
import { getSuperTuscanData } from '../../data/regions/supertuscan';
import { getPouillesData } from '../../data/regions/pouilles';
import { getEmilieRomagneData } from '../../data/regions/emilie-romagne';
import { getLombardieData } from '../../data/regions/lombardie';
import { getTrentinHautAdigeData } from '../../data/regions/trentin-haut-adige';
import { getFrioulVenetieJulienneData } from '../../data/regions/frioul-venetie-julienne';
import { getAbruzzesData } from '../../data/regions/abruzzes';
import { getMarchesData } from '../../data/regions/marches';
import { getCampanieData } from '../../data/regions/campanie';
import { getMendozaData } from '../../data/regions/mendoza';
import { getBarossaValleyData } from '../../data/regions/barossa-valley';
import { getCreteData } from '../../data/regions/crete';
import { getMacedoniaData } from '../../data/regions/macedonia';
import { getPeloponneseData } from '../../data/regions/peloponnese';
import { getMarlboroughData } from '../../data/regions/marlborough';
import { getHawkesBayData } from '../../data/regions/hawkes-bay';
import { getCentralOtagoData } from '../../data/regions/central-otago';
import { getWairarapaData } from '../../data/regions/wairarapa';
import { getCanterburyData } from '../../data/regions/canterbury';
import { getNelsonData } from '../../data/regions/nelson';
import { getNiederoesterreichData } from '../../data/regions/niederoesterreich';
import { getBurgenlandData } from '../../data/regions/burgenland';
import { getSteiermarkData } from '../../data/regions/steiermark';
import { getWienData } from '../../data/regions/wien';
import { getSouthEasternAustraliaData } from '../../data/regions/south-eastern-australia';
// Add missing region imports
import { getHunterValleyData } from '../../data/regions/hunter-valley';
import { getMargaretRiverData } from '../../data/regions/margaret-river';
import { getMcLarenValeData } from '../../data/regions/mclaren-vale';
import { getYarraValleyData } from '../../data/regions/yarra-valley';
import { getClareValleyData } from '../../data/regions/clare-valley';
import { getCoonawarraData } from '../../data/regions/coonawarra';
import { getAdelaideHillsData } from '../../data/regions/adelaide-hills';
import { getMorningtonPeninsulaData } from '../../data/regions/mornington-peninsula';
import { getTasmanieData } from '../../data/regions/tasmanie';
import { getAutresAustralieData } from '../../data/regions/autres-australie';
import { getBajaCaliforniaData } from '../../data/regions/baja-california';
import { getCoahuilaData } from '../../data/regions/coahuila';
import { getQueretaroData } from '../../data/regions/queretaro';
import { getAutresData } from '../../data/regions/autres';

// Data mapping object - using functions
const regionDataMap = {
  'provence': getProvenceData,
  'bordeaux': getBordeauxData,
  'bourgogne': getBourgogneData,
  'champagne': getChampagneData,
  'loire': getLoireData,
  'rhone': getRhoneData,
  'languedoc-roussillon': getLanguedocRoussillonData,
  'alsace': getAlsaceData,
  'jura': getJuraData,
  'sud-ouest': getSudOuestData,
  'rioja': getRiojaData,
  'ribera-del-duero': getRiberaDelDueroData,
  'navarra': getNavarraData,
  'priorat': getPrioratData,
  'rias-baixas': getRiasBaixasData,
  'rueda': getRuedaData,
  'cava': getCavaData,
  'jerez': getJerezData,
  'douro': getDouroData,
  'alentejo': getAlentejoData,
  'dao': getDaoData,
  'vinho-verde': getVinhoVerdeData,
  'bairrada': getBairradaData,
  'lisboa': getLisboaData,
  'setubal': getSetubalData,
  'madeira': getMadeiraData,
  'toscane': getToscaneData,
  'piemont': getPiemontData,
  'venetie': getVenetieData,
  'sicile': getSicileData,
  'supertuscan': getSuperTuscanData,
  'pouilles': getPouillesData,
  'emilie-romagne': getEmilieRomagneData,
  'lombardie': getLombardieData,
  'trentin-haut-adige': getTrentinHautAdigeData,
  'frioul-venetie-julienne': getFrioulVenetieJulienneData,
  'abruzzes': getAbruzzesData,
  'marches': getMarchesData,
  'campanie': getCampanieData,
  'mendoza': getMendozaData,
  'barossa-valley': getBarossaValleyData,
  'crete': getCreteData,
  'macedonia': getMacedoniaData,
  'peloponnese': getPeloponneseData,
  'marlborough': getMarlboroughData,
  'hawkes-bay': getHawkesBayData,
  'central-otago': getCentralOtagoData,
  'wairarapa': getWairarapaData,
  'canterbury': getCanterburyData,
  'nelson': getNelsonData,
  'niederoesterreich': getNiederoesterreichData,
  'burgenland': getBurgenlandData,
  'steiermark': getSteiermarkData,
  'wien': getWienData,
  'south-eastern-australia': getSouthEasternAustraliaData,
  // Add missing regions to the map
  'hunter-valley': getHunterValleyData,
  'margaret-river': getMargaretRiverData,
  'mclaren-vale': getMcLarenValeData,
  'yarra-valley': getYarraValleyData,
  'clare-valley': getClareValleyData,
  'coonawarra': getCoonawarraData,
  'adelaide-hills': getAdelaideHillsData,
  'mornington-peninsula': getMorningtonPeninsulaData,
  'tasmanie': getTasmanieData,
  'autres-australie': getAutresAustralieData,
  'baja-california': getBajaCaliforniaData,
  'coahuila': getCoahuilaData,
  'queretaro': getQueretaroData,
  'autres': getAutresData,
};

export const RegionInfoPage = () => {
  const { country, region } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Normalize region parameter to handle accented characters
  const normalizeRegionParam = (regionParam) => {
    if (!regionParam) return null;
    
    // Convert accented characters to their non-accented equivalents
    const normalized = regionParam
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^a-z0-9]/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    return normalized;
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // Handle both old and new URL structures
    let regionKey;
    if (region && country) {
      // New structure: /info/:country/regions/:region
      regionKey = normalizeRegionParam(region);
    } else if (region) {
      // Old structure: /info/regions/:region (fallback)
      regionKey = normalizeRegionParam(region);
    } else {
      setError('URL invalide');
      setLoading(false);
      return;
    }
    
    console.log('Debug - country:', country);
    console.log('Debug - region (original):', region);
    console.log('Debug - regionKey (normalized):', regionKey);
    console.log('Debug - available keys:', Object.keys(regionDataMap));
    console.log('Debug - regionDataMap[regionKey]:', regionDataMap[regionKey]);
    
    const getRegionData = regionDataMap[regionKey];
    
    if (getRegionData) {
      setData(getRegionData());
      setLoading(false);
    } else {
      // Show "no info available" message instead of redirecting to 404
      setError('no-info');
      setLoading(false);
    }
  }, [country, region, navigate]);

  if (loading) {
    return <main style={{ padding: 40, textAlign: 'center' }}>Chargement...</main>;
  }
  
  if (error === 'no-info') {
    return (
      <main style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <Breadcrumb />
        
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px', paddingBottom: '16px', borderBottom: '1px solid #e8e0e8' }}>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
            onMouseOver={e => e.currentTarget.style.background = '#f0e6f0'}
            onMouseOut={e => e.currentTarget.style.background = 'none'}
          >
            <ArrowLeft size={24} color="#4B164C" />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '32px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '700' }}>{region}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '16px' }}>
              <MapPin size={16} />
              <span>{country}</span>
            </div>
          </div>
        </div>

        {/* No Info Available Message */}
        <section style={{ 
          background: '#f8f6fa', 
          borderRadius: '16px', 
          padding: '48px 32px', 
          boxShadow: '0 4px 24px rgba(75,22,76,0.08)',
          textAlign: 'center'
        }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: '#4B164C', 
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto'
          }}>
            <MapPin size={32} color="#fff" />
          </div>
          <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600' }}>
            Informations non disponibles
          </h2>
          <p style={{ fontSize: '18px', lineHeight: '1.6', color: '#666', margin: '0 0 32px 0', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
            Les informations pour la région <strong>{region}</strong> ne sont pas encore disponibles dans notre base de données. 
            Nous travaillons continuellement à enrichir notre guide du vin.
          </p>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
            <button 
              onClick={() => navigate(`/info/countries/${country}/regions`)}
              style={{
                background: '#4B164C',
                color: '#fff',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
              onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
            >
              Explorer d'autres régions
            </button>
            <button 
              onClick={() => navigate(`/info/countries/${country}`)}
              style={{
                background: '#f0e6f0',
                color: '#4B164C',
                border: '1px solid #4B164C',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#e0d6e0'}
              onMouseOut={e => e.currentTarget.style.background = '#f0e6f0'}
            >
              Retour au pays
            </button>
            <button 
              onClick={() => navigate('/info/countries')}
              style={{
                background: '#f0e6f0',
                color: '#4B164C',
                border: '1px solid #4B164C',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#e0d6e0'}
              onMouseOut={e => e.currentTarget.style.background = '#f0e6f0'}
            >
              Tous les pays
            </button>
          </div>
        </section>
      </main>
    );
  }
  
  if (error) {
    return (
      <main style={{ padding: 40, textAlign: 'center' }}>
        <h1>Erreur</h1>
        <p>Une erreur s'est produite lors du chargement des données.</p>
        <div style={{ margin: '2rem 0' }}>
          <button onClick={() => navigate(-1)} style={{ marginRight: 16, background: '#4B164C', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}>Retour</button>
          <button onClick={() => navigate('/')} style={{ background: '#f0e6f0', color: '#4B164C', border: '1px solid #4B164C', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}>Accueil</button>
        </div>
      </main>
    );
  }
  if (!data) return null;

  return (
    <main style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Breadcrumb />
      
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px', paddingBottom: '16px', borderBottom: '1px solid #e8e0e8' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
          onMouseOver={e => e.currentTarget.style.background = '#f0e6f0'}
          onMouseOut={e => e.currentTarget.style.background = 'none'}
        >
          <ArrowLeft size={24} color="#4B164C" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '32px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '700' }}>{data.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '16px' }}>
            <MapPin size={16} />
            <span>{data.country}</span>
          </div>
        </div>
        <button
          onClick={() => navigate(`/search?pays_origine=${encodeURIComponent(data.country)}&region_origine=${encodeURIComponent(data.name)}`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            background: '#4B164C',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            transition: 'background 0.2s'
          }}
          onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
          onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
        >
          <MagnifyingGlassPlus size={18} />
          Rechercher des vins
        </button>
      </div>

      {/* Main Content */}
      <div style={{ display: 'grid', gap: '32px' }}>
        {/* Description */}
        <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0', fontWeight: '600' }}>Description</h2>
            <button
              onClick={() => navigate(`/info/${country}/regions/${region}/cepages`)}
              style={{
                background: '#4B164C',
                color: '#fff',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'background 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
              onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
            >
              Voir les cépages
              <Wine size={14} />
            </button>
          </div>
          <p style={{ fontSize: '18px', lineHeight: '1.6', color: '#333', margin: '0' }}>{data.description}</p>
        </section>

        {/* Interactive Map */}
        {data.mapAreas && (
          <InteractiveWineMap 
            mapAreas={data.mapAreas} 
            country={data.country}
            regionName={data.name}
            subregions={data.subregions}
            height={400}
          />
        )}

        {/* Statistics */}
        {data.statistics && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600' }}>Statistiques</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
              {Object.entries(data.statistics).map(([key, value]) => (
                <div key={key} style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e8e0e8', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', color: '#4B164C', fontWeight: '700', marginBottom: '8px' }}>{value}</div>
                  <div style={{ fontSize: '14px', color: '#666', textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Subregions */}
        {data.subregions && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600' }}>Appellations</h2>
            <div style={{ display: 'grid', gap: '24px' }}>
              {Object.entries(data.subregions).map(([key, subregion]) => (
                <div key={key} style={{ background: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e8e0e8' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0', fontWeight: '600' }}>{subregion.name}</h3>
                    <button
                      onClick={() => navigate(`/info/countries/${country}/regions/${region}/${key}`)}
                      style={{
                        background: '#4B164C',
                        color: '#fff',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
                      onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
                    >
                      Voir détails
                      <MapPin size={14} />
                    </button>
                  </div>
                  <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#333', margin: '0 0 20px 0' }}>{subregion.description}</p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                    {/* Characteristics */}
                    <div>
                      <h4 style={{ fontSize: '16px', color: '#4B164C', margin: '0 0 12px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Leaf size={16} />
                        Caractéristiques
                      </h4>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {typeof subregion.characteristics === 'string' ? (
                          <p style={{ fontSize: '14px', color: '#333', margin: '0' }}>{subregion.characteristics}</p>
                        ) : (
                          Object.entries(subregion.characteristics).map(([charKey, charValue]) => (
                            <div key={charKey}>
                              <strong style={{ fontSize: '14px', color: '#4B164C' }}>{charKey.charAt(0).toUpperCase() + charKey.slice(1)}:</strong>
                              <span style={{ fontSize: '14px', color: '#333', marginLeft: '8px' }}>{charValue}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Grape Varieties */}
                    {subregion.grapeVarieties && (
                      <div>
                        <h4 style={{ fontSize: '16px', color: '#4B164C', margin: '0 0 12px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Wine size={16} />
                          Cépages
                        </h4>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {Object.entries(subregion.grapeVarieties).map(([color, varieties]) => (
                            <div key={color}>
                              <strong style={{ fontSize: '14px', color: '#4B164C', textTransform: 'capitalize' }}>{color}:</strong>
                              <span style={{ fontSize: '14px', color: '#333', marginLeft: '8px' }}>
                                {Array.isArray(varieties) ? varieties.join(', ') : String(varieties)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Wine Styles */}
                    {subregion.wineStyles && (
                      <div>
                        <h4 style={{ fontSize: '16px', color: '#4B164C', margin: '0 0 12px 0', fontWeight: '600' }}>Styles de Vins</h4>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {Object.entries(subregion.wineStyles).map(([style, description]) => (
                            <div key={style}>
                              <strong style={{ fontSize: '14px', color: '#4B164C', textTransform: 'capitalize' }}>{style}:</strong>
                              <span style={{ fontSize: '14px', color: '#333', marginLeft: '8px' }}>{description}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Food Pairing */}
                    {subregion.foodPairing && (
                      <div>
                        <h4 style={{ fontSize: '16px', color: '#4B164C', margin: '0 0 12px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ForkKnife size={16} />
                          Accords Mets & Vins
                        </h4>
                        <p style={{ fontSize: '14px', color: '#333', margin: '0' }}>{subregion.foodPairing}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* History */}
        {data.history && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600' }}>Histoire</h2>
            <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#333', margin: '0' }}>{data.history}</p>
          </section>
        )}

        {/* Continue Exploring Navigation */}
        <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
          <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600', textAlign: 'center' }}>
            Continuez à Explorer
          </h2>
          
          <div style={{ display: 'grid', gap: '24px' }}>
            {/* Subregions Section */}
            {data.subregions && (
              <div>
                <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPin size={20} />
                  Appellations de {data.name}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                  {Object.entries(data.subregions).slice(0, 6).map(([key, subregion]) => (
                    <button
                      key={key}
                      onClick={() => navigate(`/info/countries/${country}/regions/${region}/${key}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        background: '#fff',
                        border: '2px solid #e8e0e8',
                        borderRadius: '12px',
                        padding: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'left',
                        width: '100%'
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.borderColor = '#4B164C';
                        e.currentTarget.style.background = '#f0e6f0';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.borderColor = '#e8e0e8';
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        background: '#4B164C', 
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        {subregion.name.charAt(0)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#4B164C', marginBottom: '4px' }}>
                          {subregion.name}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.4' }}>
                          {subregion.description.substring(0, 80)}...
                        </div>
                      </div>
                    </button>
                  ))}
                  {Object.keys(data.subregions).length > 6 && (
                    <button
                      onClick={() => navigate(`/info/countries/${country}/regions/${region}/subregions`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        background: '#4B164C',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '16px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '500',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
                      onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
                    >
                      Voir toutes les appellations ({Object.keys(data.subregions).length})
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Grape Varieties Section */}
            <div>
              <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Wine size={20} />
                Cépages de {data.name}
              </h3>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                background: '#fff',
                border: '2px solid #e8e0e8',
                borderRadius: '12px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => navigate(`/info/countries/${country}/regions/${region}/cepages`)}
              onMouseOver={e => {
                e.currentTarget.style.borderColor = '#4B164C';
                e.currentTarget.style.background = '#f0e6f0';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.borderColor = '#e8e0e8';
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{ 
                  width: '50px', 
                  height: '50px', 
                  background: '#4B164C', 
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px'
                }}>
                  🍇
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#4B164C', marginBottom: '4px' }}>
                    Découvrir les Cépages
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    Explorez tous les cépages cultivés dans cette région
                  </div>
                </div>
                <div style={{ fontSize: '24px', color: '#4B164C' }}>→</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '12px', 
              justifyContent: 'center',
              marginTop: '16px'
            }}>
              <button
                onClick={() => navigate(`/info/countries/${country}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: '#6d2c7a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#8a4a8a'}
                onMouseOut={e => e.currentTarget.style.background = '#6d2c7a'}
              >
                <MapPin size={18} />
                Retour à {data.country}
              </button>
              <button
                onClick={() => navigate('/info/countries')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: '#f0e6f0',
                  color: '#4B164C',
                  border: '2px solid #4B164C',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = '#4B164C';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = '#f0e6f0';
                  e.currentTarget.style.color = '#4B164C';
                }}
              >
                <Globe size={18} />
                Tous les Pays
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}; 